











import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { ILinkedPageObjectSpecifiable, ILinkedObjectSpecifiable } from "../../../../domain/format/objects";
import { CommonComponentsContainer } from "../../../../../formatEditor/components/format/editors/contents";
import { FormatObjectSelectorColumnTypes } from "../../../../../formatEditor/components/format/editors/contents/commonComponents/selectors";
import { IFormatObjectEditable } from "../../../../../formatEditor/domain/editableFormat";
import { instanceOfIScriptExecuteTriggerBehaviorPropertyEditable } from "../../../../../formatEditor/domain/editableFormat/properties";

@Component
export default class ScriptExecuteTriggerBehaviorEditorFromScriptlink extends Mixins() {
  @Prop()
  linkedObjectIdentifier: ILinkedObjectSpecifiable;

  @Prop()
  commonComponents: CommonComponentsContainer;

  get pageKey(): number {
    return this.commonComponents.currentPage.pageKey().getValue();
  }

  get currentLinkedObjectIdentifier(): ILinkedPageObjectSpecifiable {
    return {
      pageKey: this.pageKey,
      type: this.linkedObjectIdentifier.type,
      id: this.linkedObjectIdentifier.id,
      key: this.linkedObjectIdentifier.key,
      subKey: this.linkedObjectIdentifier.subKey,
      tableIndex: this.linkedObjectIdentifier.tableIndex,
      isDate: this.linkedObjectIdentifier.isDate,
    };
  }

  get targets(): ILinkedPageObjectSpecifiable[] {
    return Enumerable.from(this.commonComponents.allPagesFormatObjectsForEachPage)
      .selectMany(p => Enumerable.from(p.objects)
        .where(o => this.conditionsOfEachTarget(o))
        .select(o => this.toLinkedPageObjectSpecifiable(o, p.pageKey))
        .toArray())
      .toArray();
  }

  private conditionsOfEachTarget(object: IFormatObjectEditable): boolean {
    if (!instanceOfIScriptExecuteTriggerBehaviorPropertyEditable(object) || !object.scriptExecuteTriggerBehavior().isEditable()) {
      return false;
    }
    return Enumerable.from(object.scriptExecuteTriggerBehavior().getValue().targets)
      .any(t => this.compareLinkedPageObjectSpecifiable(t, this.currentLinkedObjectIdentifier));
  }

  private compareLinkedPageObjectSpecifiable(a: ILinkedPageObjectSpecifiable, b: ILinkedPageObjectSpecifiable): boolean {
    return a.pageKey === b.pageKey &&
      a.type === b.type &&
      a.id === b.id &&
      a.key === b.key &&
      a.subKey === b.subKey &&
      (a.tableIndex ? a.tableIndex.row : null) === (b.tableIndex ? b.tableIndex.row : null) &&
      (a.tableIndex ? a.tableIndex.column : null) === (b.tableIndex ? b.tableIndex.column : null) &&
      a.isDate === b.isDate;
  }

  get selectionObjects(): ILinkedPageObjectSpecifiable[] {
    return Enumerable.from(this.commonComponents.allPagesFormatObjectsForEachPage)
      .selectMany(p => Enumerable.from(p.objects)
        .where(o => this.conditionsOfEachElementType(o))
        .select(o => this.toLinkedPageObjectSpecifiable(o, p.pageKey))
        .toArray())
      .toArray();
  }

  private conditionsOfEachElementType(object: IFormatObjectEditable): boolean {
    if (!instanceOfIScriptExecuteTriggerBehaviorPropertyEditable(object) || !object.scriptExecuteTriggerBehavior().isEditable()) {
      return false;
    }
    const linkedObject = object.toLinkedObjectIdentifier();
    switch (object.objectTypeName().getValue()) {
      case "check":
      case "circleselect":
        return !!linkedObject.id && !!linkedObject.key && linkedObject.key !== "" && !!linkedObject.subKey && linkedObject.subKey !== "";
      default:
        return !!linkedObject.id && !!linkedObject.key && linkedObject.key !== "";
    }
  }

  private toLinkedPageObjectSpecifiable(object: IFormatObjectEditable, pageKey: number): ILinkedPageObjectSpecifiable {
    const linkedObject = object.toLinkedObjectIdentifier();
    return {
      pageKey: pageKey,
      type: linkedObject.type,
      id: linkedObject.id,
      key: linkedObject.key,
      subKey: linkedObject.subKey,
      tableIndex: linkedObject.tableIndex,
      isDate: linkedObject.isDate,
    };
  }

  async showScriptExecuteTriggerEditDialog() {
    const dialog = this.commonComponents.getDialogs().getFormatObjectSelectDialog();
    const targets = await dialog.show(
      this.targets,
      this.selectionObjects,
      ["selection", "type", "key", "subKey", "id", "pageNumber"],
      ["selection", "type", "key", "subKey", "id", "pageNumber"],
      false,
      false,
      [
        { enabled: false, name: "テキスト", description: "テキストで絞り込みします", filter: v => v.type === "text" || v.type === "tag" },
        { enabled: false, name: "日付", description: "日付で絞り込みします", filter: v => v.type === "date" && v.isDate === true },
        { enabled: false, name: "時間", description: "時間で絞り込みします", filter: v => v.type === "date" && v.isDate === false },
        { enabled: false, name: "チェック", description: "チェックで絞り込みします", filter: v => v.type === "check" },
        { enabled: false, name: "円選択", description: "円選択で絞り込みします", filter: v => v.type === "circleselect" },
        { enabled: false, name: "リスト", description: "リストで絞り込みします", filter: v => v.type === "combo" },
        { enabled: false, name: "クリック", description: "クリック選択で絞り込みします", filter: v => v.type === "roll" },
        { enabled: false, name: "外部データ", description: "外部データで絞り込みします", filter: v => v.type === "script" },
        { enabled: false, name: "画像", description: "画像で絞り込みします", filter: v => v.type === "imagearea" },
      ],
      this.toCellString);
    if (!targets) { return; }
    this.updateTargets(targets);
  }

  private toCellString(column: FormatObjectSelectorColumnTypes, row: ILinkedPageObjectSpecifiable): string {
    const formatObject = this.getFormatObjectEditable(row);
    switch (column) {
      case "type":
        return formatObject ? formatObject.getTypeName() + (!!row.tableIndex ? "[" + row.tableIndex.row + "][" + row.tableIndex.column + "]" : "") : "";
      case "id":
        return row.id ? row.id.toString() : "";
      case "key":
        return row.key; // formatObject.getIdentifiableName()を出したほうが良いと思われるが要検討
      case "subKey":
        return row.subKey ? row.subKey : "";
      case "pageNumber":
        const pageNumber = this.commonComponents.getPageNumber(row.pageKey);
        return pageNumber === 0 ? "削除済" : pageNumber.toString();
      default:
        return "";
    }
  }

  private getFormatObjectEditable(row: ILinkedPageObjectSpecifiable): IFormatObjectEditable {
    const pageFormatObjects = this.commonComponents.allPagesFormatObjectsForEachPage.find(p => p.pageKey === row.pageKey);
    if (!pageFormatObjects) {
      return null;
    }
    return Enumerable.from(pageFormatObjects.objects)
      .firstOrDefault(o => this.compareLinkedPageObjectSpecifiable(this.toLinkedPageObjectSpecifiable(o, pageFormatObjects.pageKey), row));
  }

  private updateTargets(targets: ILinkedPageObjectSpecifiable[]) {
    this.commonComponents.updateAllPagesFormatObjects(
      (s, k) => instanceOfIScriptExecuteTriggerBehaviorPropertyEditable(s) && s.scriptExecuteTriggerBehavior().isEditable(),
      (s, k) => {
        if (instanceOfIScriptExecuteTriggerBehaviorPropertyEditable(s) && s.scriptExecuteTriggerBehavior().isEditable()) {
          const ts = s.scriptExecuteTriggerBehavior().getValue().targets.concat()
            .filter(v => !this.compareLinkedPageObjectSpecifiable(v, this.currentLinkedObjectIdentifier));
          if (Enumerable.from(targets)
            .any(t => this.compareLinkedPageObjectSpecifiable(t, this.toLinkedPageObjectSpecifiable(s, k)))) {
            ts.splice(ts.length, 0, this.currentLinkedObjectIdentifier);
          }
          s.scriptExecuteTriggerBehavior().setValue({
            targets: ts,
          });
        }
      });
  }
}
